import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import HabitChart from "./HabitChart";
import Stats from "./Stats";

const HabitTracker = () => {
  const { currentUser } = useAuth();
  const [habits, setHabits] = useState([]);
  const [habitName, setHabitName] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [visibleDelete, setVisibleDelete] = useState(null);
  const habitRef = useRef(null); // Reference to detect outside clicks

  useEffect(() => {
    if (!currentUser) return;

    const fetchHabits = async () => {
      const habitsRef = collection(db, "users", currentUser.uid, "habits");
      const snapshot = await getDocs(habitsRef);
      const allHabits = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const today = new Date(selectedDate);
      const dayOfWeek = today.toLocaleDateString("en-US", { weekday: "short" });

      const filteredHabits = allHabits.filter((habit) =>
        habit.days.includes(dayOfWeek)
      );

      setHabits(filteredHabits);
    };

    fetchHabits();
  }, [currentUser, selectedDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (habitRef.current && !habitRef.current.contains(event.target)) {
        setVisibleDelete(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addHabit = async () => {
    if (!habitName || selectedDays.length === 0) return;
    try {
      const habitsRef = collection(db, "users", currentUser.uid, "habits");
      await addDoc(habitsRef, {
        name: habitName,
        days: selectedDays,
        completedDates: {}, // Tracks completed status per date
      });
      setHabitName("");
      setSelectedDays([]);
    } catch (error) {
      console.error("Error adding habit:", error);
    }
  };

  const toggleCompletion = async (id, date) => {
    try {
      const habitDoc = doc(db, "users", currentUser.uid, "habits", id);
      const habit = habits.find((h) => h.id === id);
      const completedDates = habit.completedDates || {};
      const updatedCompletedDates = {
        ...completedDates,
        [date]: !completedDates[date],
      };

      await updateDoc(habitDoc, { completedDates: updatedCompletedDates });

      setHabits((prevHabits) =>
        prevHabits.map((h) =>
          h.id === id ? { ...h, completedDates: updatedCompletedDates } : h
        )
      );
    } catch (error) {
      console.error("Error updating habit:", error);
    }
  };

  const deleteHabit = async (id) => {
    try {
      const habitDoc = doc(db, "users", currentUser.uid, "habits", id);
      await deleteDoc(habitDoc);
      setVisibleDelete(null);
    } catch (error) {
      console.error("Error deleting habit:", error);
    }
  };

  const handleRightClick = (e, id) => {
    e.preventDefault();
    setVisibleDelete((prevId) => (prevId === id ? null : id));
  };

  const handleDaySelection = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleTodayOnly = () => {
    setSelectedDays([]);
    setShowFrequencyDropdown(false); // Close dropdown after selection
  };

  const handleEveryday = () => {
    setSelectedDays(["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]);
    setShowFrequencyDropdown(false); // Close dropdown after selection
  };

  const handleDateChange = (days) => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + days);
    setSelectedDate(currentDate.toISOString().split("T")[0]);
  };

  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  return (
    <Container>
      <h2>Habit Tracker</h2>

      <HabitList>
        {habits.map((habit) => (
          <Habit
            key={habit.id}
            ref={habitRef}
            onContextMenu={(e) => handleRightClick(e, habit.id)}
          >
            <div>
              <HabitName
                completed={habit.completedDates?.[selectedDate] || false}
              >
                {habit.name}
              </HabitName>
              <Frequency>
                Days:{" "}
                {habit.days.length === 7 ? "Everyday" : habit.days.join(", ")}
              </Frequency>
            </div>

            <Status>
              <CompleteButton
                completed={habit.completedDates?.[selectedDate] || false}
                onClick={() => toggleCompletion(habit.id, selectedDate)}
              >
                {habit.completedDates?.[selectedDate] ? "Undo" : "Complete"}
              </CompleteButton>
            </Status>

            {visibleDelete === habit.id && (
              <DeleteButton onClick={() => deleteHabit(habit.id)}>
                Delete
              </DeleteButton>
            )}
          </Habit>
        ))}
      </HabitList>

      {/* Add Habit Chart Component */}

      <MinimalForm>
        <Input
          type="text"
          placeholder="New Habit"
          value={habitName}
          onChange={(e) => setHabitName(e.target.value)}
        />
        <FrequencyDropdown>
          <DropdownToggle
            onClick={() => setShowFrequencyDropdown(!showFrequencyDropdown)}
          >
            {selectedDays.length === 7
              ? "Everyday"
              : selectedDays.length > 0
              ? `Selected Days: ${selectedDays.join(", ")}`
              : "Today Only"}
          </DropdownToggle>
          {showFrequencyDropdown && (
            <DropdownMenu>
              <DropdownItem
                key="Everyday"
                selected={selectedDays.length === 7}
                onClick={handleEveryday}
              >
                Everyday
              </DropdownItem>
              <DropdownItem
                key="Today Only"
                selected={selectedDays.length === 0}
                onClick={handleTodayOnly}
              >
                Today Only
              </DropdownItem>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <DropdownItem
                  key={day}
                  selected={selectedDays.includes(day)}
                  onClick={() => handleDaySelection(day)}
                >
                  {day}
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </FrequencyDropdown>
        <Button onClick={addHabit}>Add</Button>
      </MinimalForm>

      <DateSelector>
        <ArrowButton onClick={() => handleDateChange(-1)}>&lt;</ArrowButton>
        <DateDisplay>{formatDate(selectedDate)}</DateDisplay>
        <ArrowButton onClick={() => handleDateChange(1)}>&gt;</ArrowButton>
      </DateSelector>
      <HabitChart habits={habits} selectedDate={selectedDate} />
    </Container>
  );
};

// Styled Components
const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 35%;
  padding: 40px;
  background-color: #171c29;
  border-radius: 20px;
  font-family: "Comfortaa";
  h2 {
    font-weight: 300;
    text-align: center;
    margin-bottom: 20px;
    color: white;
  }
  @media (max-width: 1440px) {
    width: 59%;
  }
  @media (max-width: 850px) {
    width: 100%;
  }
  @media (max-width: 500px) {
    padding: 40px 15px;
  }
`;

const HabitList = styled.div`
  animation: ${fadeIn} 0.3s ease;
`;

const Habit = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 10px 15px;
  background: ${(props) => (props.completed ? "#20273a" : "#20273a")};
  border-radius: 15px;
  color: white;
  animation: ${fadeIn} 0.3s ease;
`;

const HabitName = styled.span`
  font-size: 21px;
  color: ${(props) => (props.completed ? "#5a5a5a" : "#ffffff")};
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
`;

const Frequency = styled.p`
  font-size: 15px;
  color: #a0a0a0;
  font-family: "Comfortaa";
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CompleteButton = styled.button`
  padding: 8px 10px;
  font-size: 14px;
  background: ${(props) => (props.completed ? "#8d2b274e" : "#ffffff")};
  color: ${(props) => (props.completed ? "#ffffff" : "#303030")};
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-family: "Comfortaa";
  @media (max-width: 500px) {
    width: 100%;
  }
`;

const MinimalForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  width: 100%;
  margin-top: 2em;
  font-family: "Comfortaa";
`;

const Input = styled.input`
  width: 60%;
  padding: 15px;
  font-size: 14px;
  border-radius: 15px;
  border: none;
  background-color: #3b445c;
  color: white;
  outline: none;
  font-family: "Comfortaa";
  /* margin-bottom: 10px; */
  @media (max-width: 500px) {
    width: 60%;
  }
`;

const FrequencyDropdown = styled.div`
  position: relative;
  width: 2%;
  min-width: 110px;
  font-family: "Comfortaa";
`;

const DropdownToggle = styled.button`
  width: 100%;
  padding: 15px 10px;
  font-size: 14px;
  background-color: #3b445c;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: "Comfortaa";
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #3b445c;
  border-radius: 10px;
  padding: 10px 7px;
  width: 100%;
  z-index: 2;
`;

const DropdownItem = styled.div`
  padding: 10px;
  cursor: pointer;
  background-color: ${(props) => (props.selected ? "#ffffff" : "#3b445c")};
  color: ${(props) => (props.selected ? "#303030" : "#ffffff")};
  &:hover {
    opacity: 0.9;
  }
`;

const Button = styled.button`
  padding: 15px 25px;
  font-size: 14px;
  background: #e7e7e7;
  color: #252525;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  @media (max-width: 500px) {
    width: 100%;
    margin-top: 10px;
  }
`;

const DateSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 10px;
  color: white;
  width: 57%;
  min-width: 200px;
  margin: auto;
  margin-top: 2em;
  @media (max-width: 720px) {
    width: 100%;
  }
`;

const ArrowButton = styled.button`
  background-color: #3b445c;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 18px;

  &:hover {
    background-color: #5a647c;
  }
`;

const DateDisplay = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  flex: 1;
  font-weight: 200;
`;

const DeleteButton = styled.button`
  padding: 8px 10px;
  font-size: 14px;
  background: #d9534f;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: #c9302c;
  }
`;

export default HabitTracker;
